import useAuth from "../../hooks/useAuth";
import React, { useState, useEffect, useMemo } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Paper,
  Divider,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Avatar,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import FastfoodIcon from "@mui/icons-material/Fastfood";
import TuneIcon from "@mui/icons-material/Tune";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DensityMediumIcon from "@mui/icons-material/DensityMedium";
import Swal from "sweetalert2";

import { MealMultiSelect, MealItem } from "./MealMultiSelect";
import { availabilityRulesApi, AvailabilityRule } from "../../api/availabilityRules";

const DAYS_MAP = [
  { day: 1, label: "Mon" },
  { day: 2, label: "Tue" },
  { day: 3, label: "Wed" },
  { day: 4, label: "Thu" },
  { day: 5, label: "Fri" },
  { day: 6, label: "Sat" },
  { day: 7, label: "Sun" },
];

interface AvailabilityRuleDrawerProps {
  open: boolean;
  onClose: () => void;
  editingRule: AvailabilityRule | null;
  allMeals: MealItem[];
  allCombos?: any[];
  existingRules: AvailabilityRule[];
  onSaved: () => Promise<void> | void;
}

export const AvailabilityRuleDrawer: React.FC<AvailabilityRuleDrawerProps> = ({
  open,
  onClose,
  editingRule,
  allMeals,
  allCombos = [],
  existingRules,
  onSaved,
}) => {
  // Form States
  const { admin, selectedKitchenId, kitchens } = useAuth();
  const [itemType, setItemType] = useState<"NowMeal" | "NowCombo">("NowMeal");
  const [selectedMealIds, setSelectedMealIds] = useState<string[]>([]);
  const [ruleName, setRuleName] = useState<string>("");
  const [scheduleType, setScheduleType] = useState<"WEEKLY" | "SPECIFIC_DATES" | "DATE_RANGE">("WEEKLY");
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([1, 2, 3, 4, 5]);
  const [specificDates, setSpecificDates] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [stockMode, setStockMode] = useState<"LIMITED" | "UNLIMITED">("LIMITED");
  const [defaultQuantity, setDefaultQuantity] = useState<number>(20);
  const [quantityMode, setQuantityMode] = useState<"UNIFORM" | "CUSTOM">("UNIFORM");
  const [mealQuantities, setMealQuantities] = useState<Record<string, number>>({});
  const [priority, setPriority] = useState<number>(10);
  const [copySourceRuleId, setCopySourceRuleId] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Initialize or reset when editingRule or open changes
  useEffect(() => {
    if (!open) return;

    if (editingRule) {
      setItemType(editingRule.itemType || "NowMeal");
      setSelectedMealIds([editingRule.mealId?._id || ""]);
      setRuleName(editingRule.ruleName || "");
      setScheduleType(editingRule.scheduleType);
      setDaysOfWeek(editingRule.daysOfWeek || []);
      setSpecificDates((editingRule.specificDates || []).join(", "));
      setStartDate(editingRule.startDate || "");
      setEndDate(editingRule.endDate || "");
      setStockMode(editingRule.stockMode);
      setDefaultQuantity(editingRule.defaultQuantity ?? 20);
      setQuantityMode("UNIFORM");
      setMealQuantities({});
      setPriority(editingRule.priority || 10);
      setCopySourceRuleId("");
    } else {
      setItemType("NowMeal");
      setSelectedMealIds([]);
      setRuleName("");
      setScheduleType("WEEKLY");
      setDaysOfWeek([1, 2, 3, 4, 5]);
      setSpecificDates("");
      setStartDate("");
      setEndDate("");
      setStockMode("LIMITED");
      setDefaultQuantity(20);
      setQuantityMode("UNIFORM");
      setMealQuantities({});
      setPriority(10);
      setCopySourceRuleId("");
    }
  }, [open, editingRule]);

  const targetItems: MealItem[] = useMemo(() => {
    if (itemType === "NowCombo") {
      return (allCombos || []).map((c: any) => ({
        _id: c._id,
        name: c.title,
        image: c.image,
        currentPrice: c.comboPrice,
        categoryId: "Combos",
        tags: ["Combo"],
      }));
    }
    return allMeals;
  }, [itemType, allMeals, allCombos]);

  // Keep mealQuantities synchronized with selectedMealIds
  useEffect(() => {
    setMealQuantities((prev) => {
      const next: Record<string, number> = {};
      selectedMealIds.forEach((id) => {
        next[id] = prev[id] !== undefined ? prev[id] : defaultQuantity;
      });
      return next;
    });
  }, [selectedMealIds, defaultQuantity]);

  // Toggle Day of Week
  const handleToggleDay = (day: number) => {
    setDaysOfWeek((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort()
    );
  };

  // Quick Template: Copy from existing rule
  const handleCopySettings = (ruleId: string) => {
    setCopySourceRuleId(ruleId);
    const source = existingRules.find((r) => r._id === ruleId);
    if (!source) return;

    setScheduleType(source.scheduleType);
    setDaysOfWeek(source.daysOfWeek || []);
    setSpecificDates((source.specificDates || []).join(", "));
    setStartDate(source.startDate || "");
    setEndDate(source.endDate || "");
    setStockMode(source.stockMode);
    setDefaultQuantity(source.defaultQuantity ?? 20);
    setPriority(source.priority || 10);
    if (!ruleName) {
      setRuleName(`Copy of ${source.ruleName || "Rule"}`);
    }
    Swal.fire({
      title: "Schedule Copied!",
      text: `Applied schedule settings from "${source.ruleName || source.mealId?.name}"`,
      icon: "success",
      timer: 1500,
      showConfirmButton: false,
    });
  };

  // Update specific dish quantity
  const handleDishQuantityChange = (dishId: string, delta: number) => {
    setMealQuantities((prev) => {
      const current = prev[dishId] !== undefined ? prev[dishId] : defaultQuantity;
      const updated = Math.max(1, current + delta);
      return { ...prev, [dishId]: updated };
    });
  };

  const handleSetDishQuantityExact = (dishId: string, val: number) => {
    const safeVal = Math.max(0, isNaN(val) ? 0 : val);
    setMealQuantities((prev) => ({ ...prev, [dishId]: safeVal }));
  };

  // Set all selected dishes to a specific amount
  const handleBulkSetQuantities = (qty: number) => {
    const updated: Record<string, number> = {};
    selectedMealIds.forEach((id) => {
      updated[id] = qty;
    });
    setMealQuantities(updated);
  };

  // Compute selected dish objects
  const selectedMeals = useMemo(() => {
    const map = new Map(allMeals.map((m) => [m._id, m]));
    return selectedMealIds.map((id) => map.get(id)).filter(Boolean) as MealItem[];
  }, [allMeals, selectedMealIds]);

  // Compute total planned daily capacity
  const totalPlannedUnits = useMemo(() => {
    if (stockMode === "UNLIMITED") return "Unlimited";
    if (quantityMode === "UNIFORM") {
      return selectedMealIds.length * defaultQuantity;
    }
    return selectedMealIds.reduce((sum, id) => sum + (mealQuantities[id] || defaultQuantity), 0);
  }, [stockMode, quantityMode, selectedMealIds, defaultQuantity, mealQuantities]);

  // Form Submission
  const handleSave = async () => {
    if (!editingRule && selectedMealIds.length === 0) {
      Swal.fire("Missing Dishes", "Please select at least one dish for this rule.", "warning");
      return;
    }

    if (scheduleType === "WEEKLY" && daysOfWeek.length === 0) {
      Swal.fire("Missing Schedule", "Please select at least one active day of the week.", "warning");
      return;
    }

    if (scheduleType === "DATE_RANGE" && (!startDate || !endDate)) {
      Swal.fire("Missing Dates", "Please specify both Start Date and End Date.", "warning");
      return;
    }

    if (scheduleType === "SPECIFIC_DATES" && !specificDates.trim()) {
      Swal.fire("Missing Dates", "Please enter at least one specific date string (YYYY-MM-DD).", "warning");
      return;
    }

    setSubmitting(true);
    try {
      const payload: any = {
        ruleName: ruleName.trim(),
        scheduleType,
        daysOfWeek: scheduleType === "WEEKLY" ? daysOfWeek : [],
        specificDates:
          scheduleType === "SPECIFIC_DATES"
            ? specificDates
                .split(",")
                .map((d) => d.trim())
                .filter(Boolean)
            : [],
        startDate: scheduleType === "DATE_RANGE" ? startDate : null,
        endDate: scheduleType === "DATE_RANGE" ? endDate : null,
        stockMode,
        defaultQuantity,
        // This form sets uniform daily capacity; stale weekday overrides must not win over it.
        dayQuantities: {},
        priority,
        itemType,
      };

      if (editingRule) {
        payload.mealId = editingRule.mealId?._id;
        await availabilityRulesApi.updateRule(editingRule._id, payload);
        Swal.fire("Success", "Availability rule updated successfully", "success");
      } else {
        payload.mealIds = selectedMealIds;
        if (admin && !["admin", "super-admin"].includes(admin.role)) payload.kitchenId = selectedKitchenId;
        if (quantityMode === "CUSTOM") {
          payload.mealQuantities = mealQuantities;
        }
        const res = await availabilityRulesApi.createRule(payload);
        Swal.fire(
          "Success",
          `${res.count || selectedMealIds.length} rule(s) created successfully`,
          "success"
        );
      }

      onClose();
      await onSaved();
    } catch (err: any) {
      console.error("Save rule error", err);
      Swal.fire("Error", err.response?.data?.message || "Failed to save rule", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
      PaperProps={{
        sx: {
          width: { xs: "100%", md: "80%", lg: "70%" },
          maxWidth: "1080px",
          bgcolor: "#ffffff",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 0 35px rgba(0,0,0,0.18)",
        },
      }}
    >
      {/* ── Fixed Drawer Header ───────────────────────────────────── */}
      <Box
        sx={{
          p: 2.5,
          px: { xs: 1.5, sm: 3.5 },
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "#f8fafc",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.5,
              bgcolor: "primary.main",
              color: "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 8px rgba(249, 115, 22, 0.3)",
            }}
          >
            <EventRepeatIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {editingRule ? "Edit Availability Rule" : "Create Availability Rule"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {editingRule
                ? "Update recurring schedule and prep capacity for this meal"
                : "Assign automated recurring schedules and prep capacities to dishes"}
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary">
              {editingRule ? (editingRule.kitchenId?.name || "Applies across all kitchens") :
                admin && !["admin", "super-admin"].includes(admin.role) ?
                  `Kitchen: ${kitchens?.find(k => k._id === selectedKitchenId)?.name || "Select a kitchen"}` : "Applies across all kitchens"}
            </Typography>
          </Box>
        </Stack>

        <IconButton onClick={onClose} size="small" sx={{ color: "text.secondary" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* ── Scrollable Body ─────────────────────────────────────────── */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          p: { xs: 1.5, sm: 3.5 },
          display: "flex",
          flexDirection: "column",
          gap: 3.5,
          bgcolor: "#fcfdfe",
        }}
      >
        {/* Quick Template Selector (Create mode only) */}
        {!editingRule && existingRules.length > 0 && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2.5,
              bgcolor: "#fffbeb",
              borderColor: "#fde68a",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box sx={{ maxWidth: 450 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FlashOnIcon sx={{ color: "#d97706", fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#92400e" }}>
                  Quick Template (Optional)
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: "#b45309", display: "block", mt: 0.25 }}>
                Clone schedule type, days, and stock mode from an existing rule to speed up creation.
              </Typography>
            </Box>

            <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 280 }, width: { xs: "100%", sm: "auto" }, flexGrow: { xs: 1, sm: 0 } }}>
              <InputLabel>Select existing rule to copy</InputLabel>
              <Select
                label="Select existing rule to copy"
                value={copySourceRuleId}
                onChange={(e) => handleCopySettings(e.target.value)}
                MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
              >
                <MenuItem value="">-- Select an existing rule --</MenuItem>
                {existingRules.map((r) => (
                  <MenuItem key={r._id} value={r._id}>
                    {r.ruleName || r.mealId?.name} ({r.scheduleType} · {r.stockMode === "UNLIMITED" ? "Unlimited" : `${r.defaultQuantity} units`})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>
        )}

        {/* ── STEP 1: TARGET DISHES SELECTION ────────────────────────── */}
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    color: "#ffffff",
                    fontSize: 12,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  1
                </Box>
                <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                  {editingRule
                    ? editingRule.itemType === "NowCombo"
                      ? "Target Meal Combo"
                      : "Target Dish"
                    : "Select Target Dishes or Combos"}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: "block" }}>
                {editingRule
                  ? "This rule applies exclusively to the item below"
                  : "Choose which dishes or meal combos this availability rule should automate"}
              </Typography>
            </Box>
          </Stack>

          {editingRule ? (
            /* Single Target Item Card in Edit Mode */
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2.5,
                display: "flex",
                alignItems: "center",
                gap: 2,
                bgcolor: "#ffffff",
                border: "1.5px solid",
                borderColor: "primary.light",
              }}
            >
              {editingRule.mealId?.image ? (
                <Box
                  component="img"
                  src={editingRule.mealId.image}
                  alt={editingRule.mealId.title || editingRule.mealId.name}
                  sx={{ width: 56, height: 56, borderRadius: 2, objectFit: "cover", border: "1px solid #e2e8f0" }}
                />
              ) : (
                <Avatar sx={{ width: 56, height: 56, bgcolor: "primary.light" }}>
                  {editingRule.itemType === "NowCombo" ? <FastfoodIcon /> : <RestaurantMenuIcon />}
                </Avatar>
              )}
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {editingRule.mealId?.title || editingRule.mealId?.name || "Item"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {editingRule.itemType === "NowCombo"
                    ? "Meal Combo"
                    : typeof editingRule.mealId?.categoryId === "object"
                    ? editingRule.mealId?.categoryId.name
                    : "Instant Meal"}{" "}
                  · ₹{editingRule.mealId?.comboPrice || editingRule.mealId?.currentPrice}
                </Typography>
              </Box>
              <Chip
                label={editingRule.itemType === "NowCombo" ? "Combo Rule" : "Dish Rule"}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            </Paper>
          ) : (
            /* Creation Mode: Classification toggle + Multi-Select */
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 1 }}>
                  ITEM CLASSIFICATION
                </Typography>
                <ToggleButtonGroup
                  value={itemType}
                  exclusive
                  onChange={(_, val) => {
                    if (val) {
                      setItemType(val);
                      setSelectedMealIds([]);
                      setMealQuantities({});
                    }
                  }}
                  size="small"
                >
                  <ToggleButton value="NowMeal" sx={{ px: { xs: 1, sm: 2 }, whiteSpace: "normal", minWidth: 0, textTransform: "none", fontWeight: 600 }}>
                    <RestaurantMenuIcon fontSize="small" sx={{ mr: 1 }} /> Main Dishes & Add-ons
                  </ToggleButton>
                  <ToggleButton value="NowCombo" sx={{ px: { xs: 1, sm: 2 }, whiteSpace: "normal", minWidth: 0, textTransform: "none", fontWeight: 600 }}>
                    <FastfoodIcon fontSize="small" sx={{ mr: 1 }} /> Meal Combos
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <MealMultiSelect
                meals={targetItems}
                selectedMealIds={selectedMealIds}
                onChange={(ids) => setSelectedMealIds(ids)}
                maxHeight={340}
              />
            </Box>
          )}
        </Box>

        {/* ── STEP 2: PREP QUANTITY TUNING ──────────────────────────── */}
        {stockMode === "LIMITED" && selectedMealIds.length > 0 && !editingRule && (
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 2.5,
              bgcolor: "#ffffff",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  color: "#ffffff",
                  fontSize: 12,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                2
              </Box>
              <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                Daily Prep Capacity Tuning
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: "block", mb: 2 }}>
              Choose whether all selected dishes share the same prep limit, or customize per dish based on demand.
            </Typography>

            {/* Mode Toggle */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5, flexWrap: "wrap", gap: 1.5 }}>
              <ToggleButtonGroup
                value={quantityMode}
                sx={{ minWidth: 0, maxWidth: "100%", "& .MuiToggleButton-root": { px: { xs: 1, sm: 2 }, whiteSpace: "normal", minWidth: 0 } }}
                exclusive
                onChange={(_, next) => next && setQuantityMode(next)}
                size="small"
              >
                <ToggleButton value="UNIFORM" sx={{ px: 2, textTransform: "none", fontWeight: 600 }}>
                  <DensityMediumIcon sx={{ fontSize: 16, mr: 0.75 }} /> Uniform (Same for all)
                </ToggleButton>
                <ToggleButton value="CUSTOM" sx={{ px: 2, textTransform: "none", fontWeight: 600 }}>
                  <TuneIcon sx={{ fontSize: 16, mr: 0.75 }} /> Custom Per-Dish Quantities
                </ToggleButton>
              </ToggleButtonGroup>

              {quantityMode === "CUSTOM" && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary">Quick actions:</Typography>
                  <Button size="small" variant="outlined" onClick={() => handleBulkSetQuantities(20)} sx={{ textTransform: "none", fontSize: 11, py: 0.2 }}>
                    All = 20
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => handleBulkSetQuantities(35)} sx={{ textTransform: "none", fontSize: 11, py: 0.2 }}>
                    All = 35
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => handleBulkSetQuantities(50)} sx={{ textTransform: "none", fontSize: 11, py: 0.2 }}>
                    All = 50
                  </Button>
                </Stack>
              )}
            </Box>

            {/* Mode Content */}
            {quantityMode === "UNIFORM" ? (
              <Box sx={{ maxWidth: 360 }}>
                <TextField
                  label="Daily Capacity for All Selected Dishes (Units)"
                  type="number"
                  fullWidth
                  size="small"
                  value={defaultQuantity}
                  onChange={(e) => setDefaultQuantity(Math.max(0, Number(e.target.value)))}
                  helperText="Each of the selected dishes will receive this daily capacity"
                />
              </Box>
            ) : (
              /* Custom Per-Dish Table / Cards */
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  overflow: "hidden",
                  bgcolor: "#f8fafc",
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    bgcolor: "#f1f5f9",
                    display: "grid",
                    gridTemplateColumns: "1fr 140px",
                    fontWeight: 700,
                    fontSize: 12,
                    color: "text.secondary",
                  }}
                >
                  <Typography variant="caption" fontWeight={700}>DISH ({selectedMeals.length})</Typography>
                  <Typography variant="caption" fontWeight={700} align="center">DAILY CAPACITY (UNITS)</Typography>
                </Box>

                <Box sx={{ maxHeight: 280, overflowY: "auto", p: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                  {selectedMeals.map((meal) => {
                    const currentQty = mealQuantities[meal._id] !== undefined ? mealQuantities[meal._id] : defaultQuantity;
                    return (
                      <Paper
                        key={meal._id}
                        variant="outlined"
                        sx={{
                          p: 1.2,
                          display: "grid",
                          gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "minmax(0, 1fr) 150px" },
                          gap: 1,
                          alignItems: "center",
                          borderRadius: 1.75,
                          bgcolor: "#ffffff",
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          {meal.image ? (
                            <Box
                              component="img"
                              src={meal.image}
                              alt={meal.name}
                              sx={{ width: 36, height: 36, borderRadius: 1.5, objectFit: "cover" }}
                            />
                          ) : (
                            <Avatar sx={{ width: 36, height: 36 }}>
                              <RestaurantMenuIcon sx={{ fontSize: 18 }} />
                            </Avatar>
                          )}
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {meal.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ₹{meal.currentPrice}
                            </Typography>
                          </Box>
                        </Stack>

                        {/* Stepper Input */}
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                          <IconButton
                            size="small"
                            onClick={() => handleDishQuantityChange(meal._id, -5)}
                            sx={{ border: "1px solid #e2e8f0" }}
                          >
                            <RemoveIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                          <TextField
                            size="small"
                            type="number"
                            value={currentQty}
                            onChange={(e) => handleSetDishQuantityExact(meal._id, Number(e.target.value))}
                            inputProps={{ style: { textAlign: "center", fontWeight: 700, padding: "5px 4px", width: "50px" } }}
                            sx={{ width: 64 }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleDishQuantityChange(meal._id, 5)}
                            sx={{ border: "1px solid #e2e8f0" }}
                          >
                            <AddIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Paper>
        )}

        {/* ── STEP 3: SCHEDULE & RECURRENCE CONFIGURATION ─────────────── */}
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            borderRadius: 2.5,
            bgcolor: "#ffffff",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                bgcolor: "primary.main",
                color: "#ffffff",
                fontSize: 12,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {stockMode === "LIMITED" && selectedMealIds.length > 0 && !editingRule ? 3 : 2}
            </Box>
            <Typography variant="subtitle1" fontWeight={700} color="text.primary">
              Schedule & Rule Parameters
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: "block", mb: 2.5 }}>
            Define the days when dishes are available, tracking mode, and resolution priority.
          </Typography>

          {/* Rule Name Input with Live Transparency Preview */}
          <Box sx={{ mb: 2.5 }}>
            <TextField
              label="Batch Rule Name / Description"
              placeholder="e.g. Standard Weekday Lunch Roster"
              fullWidth
              size="small"
              value={ruleName}
              onChange={(e) => setRuleName(e.target.value)}
            />
            {selectedMealIds.length > 1 && (
              <Box sx={{ mt: 1, px: 1.5, py: 0.75, borderRadius: 1.5, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  ℹ️ <strong>Naming Preview:</strong> Each meal's rule will be stored as:{" "}
                  <code style={{ color: "#ea580c", fontWeight: 600 }}>
                    {ruleName.trim() ? `${ruleName.trim()} — [Dish Name]` : "[Dish Name] - WEEKLY"}
                  </code>
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 2.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Schedule Recurrence Type</InputLabel>
              <Select
                label="Schedule Recurrence Type"
                value={scheduleType}
                onChange={(e) => setScheduleType(e.target.value as any)}
              >
                <MenuItem value="WEEKLY">Weekly Recurring Days (e.g. Mon–Fri)</MenuItem>
                <MenuItem value="DATE_RANGE">Continuous Date Range</MenuItem>
                <MenuItem value="SPECIFIC_DATES">Specific Calendar Dates Only</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Stock Tracking Mode</InputLabel>
              <Select
                label="Stock Tracking Mode"
                value={stockMode}
                onChange={(e) => setStockMode(e.target.value as any)}
              >
                <MenuItem value="LIMITED">Limited Stock (Fixed Sellable Portions)</MenuItem>
                <MenuItem value="UNLIMITED">Unlimited / Untracked Stock</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Weekly Days Chips */}
          {scheduleType === "WEEKLY" && (
            <Box sx={{ mb: 2.5, p: 2, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, fontWeight: 700 }}>
                Active Recurring Days of the Week:
              </Typography>
              <Stack direction="row" sx={{ gap: 1 }} flexWrap="wrap">
                {DAYS_MAP.map(({ day, label }) => {
                  const isSelected = daysOfWeek.includes(day);
                  return (
                    <Chip
                      key={day}
                      label={label}
                      clickable
                      color={isSelected ? "primary" : "default"}
                      variant={isSelected ? "filled" : "outlined"}
                      onClick={() => handleToggleDay(day)}
                      sx={{
                        fontWeight: isSelected ? 700 : 500,
                        minWidth: 50,
                        fontSize: 13,
                        py: 2,
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          )}

          {/* Date Range Picker */}
          {scheduleType === "DATE_RANGE" && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2.5 }}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <TextField
                label="End Date"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Stack>
          )}

          {/* Specific Dates */}
          {scheduleType === "SPECIFIC_DATES" && (
            <TextField
              label="Specific Dates (comma-separated YYYY-MM-DD)"
              placeholder="2026-09-25, 2026-10-02"
              fullWidth
              size="small"
              value={specificDates}
              onChange={(e) => setSpecificDates(e.target.value)}
              sx={{ mb: 2.5 }}
              helperText="E.g., for holiday specials or one-off weekend menus"
            />
          )}

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            {editingRule && stockMode === "LIMITED" ? (
              <TextField
                label="Daily Sellable Capacity (Units)"
                type="number"
                fullWidth
                size="small"
                value={defaultQuantity}
                onChange={(e) => setDefaultQuantity(Math.max(0, Number(e.target.value)))}
                helperText="Total units prepared/available per day"
              />
            ) : (
              <Box />
            )}

            <TextField
              label="Rule Precedence / Priority (Default: 10)"
              type="number"
              fullWidth
              size="small"
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value))}
              helperText="Higher priority rules override lower ones when schedules overlap."
            />
          </Box>
        </Paper>
      </Box>

      {/* ── Fixed Sticky Footer ──────────────────────────────────────── */}
      <Box
        sx={{
          p: 2.5,
          px: { xs: 1.5, sm: 3.5 },
          bgcolor: "#ffffff",
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.5,
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CheckCircleIcon sx={{ color: selectedMealIds.length > 0 ? "success.main" : "text.disabled", fontSize: 20 }} />
          <Typography variant="body2" fontWeight={600} color="text.secondary">
            {editingRule
              ? "Ready to update rule for 1 meal"
              : selectedMealIds.length > 0
              ? `${selectedMealIds.length} dish${selectedMealIds.length > 1 ? "es" : ""} selected · ${totalPlannedUnits} units planned/day`
              : "No dishes selected yet"}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1.5} sx={{ width: { xs: "100%", sm: "auto" }, "& .MuiButton-root": { flex: { xs: 1, sm: "initial" }, minWidth: 0, whiteSpace: "normal" } }}>
          <Button onClick={onClose} color="inherit" disabled={submitting} sx={{ textTransform: "none", px: 2 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={submitting || (!editingRule && selectedMealIds.length === 0)}
            sx={{ textTransform: "none", fontWeight: 700, px: 3.5, py: 1 }}
          >
            {submitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : editingRule ? (
              "Update Rule"
            ) : (
              `Create ${selectedMealIds.length} Rule${selectedMealIds.length > 1 ? "s" : ""}`
            )}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};
